import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import slugify from '@@/js/utils/slugify';
import api from '@@/js/api/api';
import cms from '@@/js/api/cms';
import metrobus from '@@/img/services/metrobus.svg';
import lebus from '@@/img/services/lebus.svg';
import express from '@@/img/services/express.svg';
import couchetard from '@@/img/services/couchetard.svg';

export const useSchedulesStore = defineStore('schedules', () => {
    const { t } = useI18n({ useScope: 'global' });
    const servicesTypesDefinition = [
        {
            code: 4,
            name: 'Métrobus',
            range: t('state.metrobus.range'),
            description: t('state.metrobus.description'),
            details: t('state.metrobus.details'),
            color: '#41851F',
            class: 'is-metrobus',
            icon: metrobus,
        },
        {
            code: 0,
            name: 'leBus',
            range: t('state.lebus.range'),
            description: t('state.lebus.description'),
            details: t('state.lebus.details'),
            color: '#003878',
            class: 'is-lebus',
            icon: lebus,
        },
        {
            code: 3,
            name: 'Parcours industriels',
            range: t('state.industriel.range'),
            description: t('state.industriel.description'),
            color: '#003878',
            mergeWith: 0,
            class: 'is-lebus',
            icon: lebus,
        },
        {
            code: 2,
            name: 'Parcours école',
            range: t('state.ecole.range'),
            description: t('state.ecole.description'),
            color: '#003878',
            class: 'is-lebus',
            showWith: 0,
            icon: lebus,
        },
        {
            code: 1,
            name: 'eXpress',
            range: t('state.express.range'),
            description: t('state.express.description'),
            details: t('state.express.details'),
            color: '#e22b13',
            class: 'is-express',
            icon: express,
        },
        {
            code: 6,
            name: 'Couche-tard',
            range: t('state.couchetard.range'),
            description: t('state.couchetard.description'),
            details: t('state.couchetard.details'),
            color: '#393936',
            class: 'is-couchetard',
            icon: couchetard,
        },
    ];

    const routes = ref([]);
    const notices = ref([]);
    const generalNotices = ref([]);
    let noticesRequest = null;

    function _mergeServices(services) {
        const serviceMap = new Map(services.map((s) => [s.code, { ...s }]));

        services.forEach((service) => {
            if (typeof service.mergeWith === 'number') {
                const target = serviceMap.get(service.mergeWith);
                if (target) {
                    target.routes.push(...service.routes);
                    serviceMap.delete(service.code);
                }
            }
        });

        Array.from(serviceMap.values()).forEach((service) => {
            service.routes.sort((a, b) => a.noParcours.localeCompare(b.noParcours, undefined, {
                numeric: true,
                sensitivity: 'base',
            }));
        });

        return serviceMap;
    }

    const routesByServices = computed(() => {
        const services = servicesTypesDefinition.map((service) => {
            const serviceRoutes = routes.value
                .filter((r) => r.codeTypeService === service.code)
                .sort((a, b) => a.noParcours.localeCompare(b.noParcours, undefined, {
                    numeric: true,
                    sensitivity: 'base',
                }));

            return {
                ...service,
                routes: serviceRoutes,
            };
        });

        const serviceMap = _mergeServices(services);

        return Array.from(serviceMap.values());
    });

    async function fetchRoute(noRoute, date) {
        let response = await api.getRoute(noRoute, true, date);
        if (!response) {
            response = await api.getRoute(noRoute, true);
        }
        return response;
    }

    // Toujours un tableau : l'API retourne null si aucun arrêt n'est en vigueur à la date demandée
    async function fetchStops(noRoute, direction, date) {
        try {
            const response = await api.getStops(noRoute, direction, date);
            return Array.isArray(response) ? response : [];
        } catch {
            return [];
        }
    }

    async function fetchStop(noStop) {
        return api.getStop(noStop);
    }

    function fetchDepartures(noRoute, noStop, direction, date) {
        return api.getDepartures(noRoute, noStop, direction, date);
    }

    function fetchTripDepartures(idDeparture, date) {
        return api.getTripDepartures(idDeparture, date);
    }

    function fetchLine(noRoute, direction, type, date) {
        return api.getParcoursTrace(
            noRoute,
            direction,
            type,
            date,
        );
    }

    function fetchRouteTransfers(noStop, noParcours, direction, date) {
        return api.getRouteTransfers(noStop, noParcours, direction, date);
    }

    function fetchStopNextDepartures(noRoute, noStop, direction, date) {
        return api.getNextDepartures(noRoute, noStop, direction, date);
    }

    function fetchStopDepartures(noStop, date) {
        return api.getStopDepartures(noStop, date);
    }

    function fetchStopsDepartures(stops, date) {
        return api.getStopsDepartures(stops, date);
    }

    // Une direction absente sur le terme du parcours signifie que l'avis vise les deux directions
    function _noticeDirectionKey(term) {
        const direction = term.code_direction;
        return direction === null || direction === undefined || direction === '' ? '*' : String(direction);
    }

    // Index `<noParcours>` et `<noParcours>:<direction|*>` des parcours visés par au moins un avis
    const routesWithNotices = computed(() => {
        const keys = new Set();
        notices.value.forEach((notice) => {
            (notice.parcours || []).forEach((parcours) => {
                (parcours.routes || []).forEach((term) => {
                    keys.add(term.name);
                    keys.add(`${term.name}:${_noticeDirectionKey(term)}`);
                });
            });
        });
        return keys;
    });

    function hasRouteNotices(noRoute, codeDirection) {
        if (codeDirection === undefined || codeDirection === null) {
            return routesWithNotices.value.has(noRoute);
        }
        return routesWithNotices.value.has(`${noRoute}:${codeDirection}`)
            || routesWithNotices.value.has(`${noRoute}:*`);
    }

    function getRouteNotices(noRoute, codeDirection) {
        const anyDirection = codeDirection === undefined || codeDirection === null;
        return notices.value.filter((notice) => (notice.parcours || []).some((parcours) => (
            (parcours.routes || []).some((term) => {
                if (term.name !== noRoute) {
                    return false;
                }
                const direction = _noticeDirectionKey(term);
                return anyDirection || direction === '*' || direction === String(codeDirection);
            })
        )));
    }

    function fetchAutocompleteStops(noStop) {
        return cms.getAutocompleteStops(noStop);
    }

    function fetchNearbyStops(location) {
        return cms.getNearbyStops(location);
    }

    function getDirectionDetails(route, direction) {
        const principale = {
            code: route.codeDirectionPrincipale,
            description: route.descriptionDirectionPrincipale,
        };
        const retour = {
            code: route.codeDirectionRetour,
            description: route.descriptionDirectionRetour,
        };
        if (!direction) {
            return principale;
        }

        // New format `<code>-<description>`: resolve by the canonical leading code.
        const codeMatch = String(direction).match(/^(\d+)-/);
        if (codeMatch) {
            return String(route.codeDirectionRetour) === codeMatch[1] ? retour : principale;
        }

        // Legacy format (slugified description only) — kept for old / bookmarked URLs.
        const slug = slugify(direction).toLowerCase();
        if (retour.description && slugify(retour.description).toLowerCase() === slug) {
            return retour;
        }
        return principale;
    }

    function getServiceType(code) {
        return servicesTypesDefinition.find((s) => s.code === code);
    }

    function getApplicationPeriods() {
        return api.getApplicationPeriods();
    }

    function sortStations(stations) {
        stations.sort((a, b) => {
            const aHasSchedules = a.horaires.length > 0 ? 1 : 0;
            const bHasSchedules = b.horaires.length > 0 ? 1 : 0;
            if (aHasSchedules === bHasSchedules) {
                const aIsDropOffOnly = a.descenteSeulement > 0 ? 1 : 0;
                const bIsDropOffOnly = b.descenteSeulement > 0 ? 1 : 0;
                if (aIsDropOffOnly === bIsDropOffOnly) {
                    return a.parcours.noParcours.localeCompare(b.parcours.noParcours, undefined, {
                        numeric: true,
                        sensitivity: 'base',
                    });
                }
                return aIsDropOffOnly - bIsDropOffOnly;
            }
            return bHasSchedules - aHasSchedules;
        });
    }

    // Les avis sont secondaires : la requête n'est jamais attendue par les pages et un échec
    // (ex. timeout) laisse simplement la liste vide. `continueOnFailure` ne suffit pas,
    // l'intercepteur CMS rejette quand même la promesse.
    function _fetchNotices() {
        if (!noticesRequest) {
            noticesRequest = cms.getAllNoticesV2()
                .then((data) => {
                    // Comme dans Drupal, un avis général ne vise aucun parcours,
                    // même si des parcours y sont saisis
                    notices.value = data.filter((notice) => !notice.general);
                    generalNotices.value = data.filter((notice) => notice.general);
                })
                .catch(() => {});
        }
        return noticesRequest;
    }

    async function _fetchRoutes() {
        if (routes.value.length === 0) {
            try {
                routes.value = (await api.getRouteList(true)) || [];
            } catch {
                routes.value = [];
            }
        }
    }

    async function fetchSchedulesData() {
        // Non attendu : les avis ne doivent pas retarder l'affichage des pages
        _fetchNotices();
        await _fetchRoutes();
    }

    return {
        routes,
        routesByServices,
        generalNotices,
        fetchDepartures,
        fetchRoute,
        fetchSchedulesData,
        fetchStops,
        fetchStop,
        fetchTripDepartures,
        fetchLine,
        fetchRouteTransfers,
        fetchStopNextDepartures,
        fetchStopDepartures,
        fetchStopsDepartures,
        getRouteNotices,
        hasRouteNotices,
        fetchAutocompleteStops,
        fetchNearbyStops,
        getDirectionDetails,
        getServiceType,
        getApplicationPeriods,
        sortStations,
    };
});
